<template>
  <div class="whatsapp-profile-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="whatsapp-profile-container">
          <div class="profile-header">
            <h1 class="page-title">WhatsApp Business Profile</h1>
            <p class="page-subtitle">Manage your WhatsApp Business profile information</p>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="loading-container">
            <div class="spinner-large"></div>
            <p>Loading profile...</p>
          </div>

          <!-- Profile Content -->
          <div v-else class="profile-content">
            <!-- Display Name Section -->
            <div class="profile-card">
              <div class="card-header">
                <h3 class="card-title">WhatsApp Business Display Name</h3>
                <p class="card-subtitle">Your verified business name shown to customers</p>
              </div>
              <div class="card-body">
                <div class="display-name-section">
                  <div class="current-display-name">
                    <label class="form-label">Current Display Name</label>
                    <div class="display-name-value">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      <span v-if="displayName" class="display-name-text">{{ displayName }}</span>
                      <span v-else class="display-name-placeholder">No display name set</span>
                      <svg v-if="displayName" class="verified-badge" width="18" height="18" viewBox="0 0 24 24" fill="#10b981">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <p v-if="phoneNumber" class="phone-number-hint">Phone: {{ phoneNumber }}</p>
                  </div>

                  <div class="new-display-name">
                    <label for="newDisplayName" class="form-label">Change Display Name</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      <input
                        id="newDisplayName"
                        v-model="newDisplayName"
                        type="text"
                        placeholder="Enter new business display name"
                        class="form-input"
                        maxlength="60"
                      />
                    </div>
                    <p class="display-name-note">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                      </svg>
                      Display name changes require Meta's approval (1-3 business days). You can request up to 10 changes per 30 days.
                    </p>
                    <button
                      @click="updateDisplayName"
                      class="update-display-name-btn"
                      :disabled="isUpdatingDisplayName || !newDisplayName || newDisplayName === displayName"
                    >
                      <span v-if="!isUpdatingDisplayName">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; margin-right: 6px;">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        Submit Name Change Request
                      </span>
                      <span v-else class="loading-spinner">
                        <span class="spinner"></span>
                        Submitting...
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Profile Photo Section -->
            <div class="profile-card">
              <div class="card-header">
                <h3 class="card-title">Profile Photo</h3>
                <p class="card-subtitle">Your current WhatsApp Business profile photo</p>
              </div>
              <div class="card-body">
                <div class="profile-photo-section">
                  <div class="photo-container">
                    <div class="photo-wrapper">
                      <img
                        v-if="profilePhotoPreview || profile.profile_picture_url"
                        :src="profilePhotoPreview || profile.profile_picture_url"
                        alt="Profile Photo"
                        class="profile-photo"
                      />
                      <div v-else class="profile-photo-placeholder">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                        </svg>
                      </div>
                      <label for="profilePictureInput" class="photo-upload-overlay">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23 4v2h-3v3h-2V6h-3V4h3V1h2v3h3zm-7 9c0-1.1-.9-2-2-2s-2 .9-2 2 .9 2 2 2 2-.9 2-2zm-2-7c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4zM6 13c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4z"/>
                        </svg>
                        <span>Change Photo</span>
                      </label>
                    </div>
                    <input
                      id="profilePictureInput"
                      type="file"
                      accept="image/jpeg,image/jpg"
                      @change="handlePhotoSelect"
                      class="photo-input-hidden"
                    />
                  </div>
                  <div class="photo-info">
                    <p class="photo-note">Profile photos must be JPG/JPEG format</p>
                    <p class="photo-note">Square (max 640x640px, 5MB max)</p>
                    <p class="photo-note">Recommended: 640x640px, minimum 63KB</p>
                    <button
                      v-if="selectedPhoto"
                      @click="uploadProfilePicture"
                      class="upload-photo-btn"
                      :disabled="isUploadingPhoto"
                    >
                      <span v-if="!isUploadingPhoto">Upload New Photo</span>
                      <span v-else class="loading-spinner">
                        <span class="spinner"></span>
                        Uploading...
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Business Information Form -->
            <form @submit.prevent="updateProfile" class="profile-form">
              <!-- Category Section -->
              <div class="profile-card">
                <div class="card-header">
                  <h3 class="card-title">Business Category</h3>
                  <p class="card-subtitle">Select your business industry category</p>
                </div>
                <div class="card-body">
                  <div class="form-group">
                    <label for="vertical" class="form-label">Category</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                      </svg>
                      <select
                        id="vertical"
                        v-model="formData.vertical"
                        class="form-input"
                        :class="{ 'error': errors.vertical }"
                      >
                        <option value="">Select a category</option>
                        <option value="AUTOMOTIVE">Automotive</option>
                        <option value="BEAUTY">Beauty, Spa & Salon</option>
                        <option value="APPAREL">Clothing & Apparel</option>
                        <option value="EDU">Education</option>
                        <option value="ENTERTAIN">Entertainment</option>
                        <option value="EVENT_PLAN">Event Planning & Service</option>
                        <option value="FINANCE">Finance & Banking</option>
                        <option value="GROCERY">Grocery & Market</option>
                        <option value="GOVT">Government</option>
                        <option value="HOTEL">Hotel & Lodging</option>
                        <option value="HEALTH">Medical & Health</option>
                        <option value="NONPROFIT">Non-profit</option>
                        <option value="PROF_SERVICES">Professional Services</option>
                        <option value="RETAIL">Shopping & Retail</option>
                        <option value="TRAVEL">Travel & Transportation</option>
                        <option value="RESTAURANT">Restaurant</option>
                        <option value="NOT_A_BIZ">Other</option>
                      </select>
                    </div>
                    <span v-if="errors.vertical" class="error-message">{{ errors.vertical }}</span>
                  </div>
                </div>
              </div>

              <!-- About Section -->
              <div class="profile-card">
                <div class="card-header">
                  <h3 class="card-title">About</h3>
                  <p class="card-subtitle">Short status message (max 139 characters)</p>
                </div>
                <div class="card-body">
                  <div class="form-group">
                    <label for="about" class="form-label">About Status</label>
                    <div class="input-wrapper">
                      <textarea
                        id="about"
                        v-model="formData.about"
                        placeholder="e.g., Succulent specialists!"
                        class="form-textarea"
                        :class="{ 'error': errors.about }"
                        maxlength="139"
                        rows="2"
                      ></textarea>
                    </div>
                    <div class="char-count">{{ formData.about.length }}/139</div>
                    <span v-if="errors.about" class="error-message">{{ errors.about }}</span>
                  </div>
                </div>
              </div>

              <!-- Description Section -->
              <div class="profile-card">
                <div class="card-header">
                  <h3 class="card-title">Business Description</h3>
                  <p class="card-subtitle">Detailed description of your business</p>
                </div>
                <div class="card-body">
                  <div class="form-group">
                    <label for="description" class="form-label">Description</label>
                    <div class="input-wrapper">
                      <textarea
                        id="description"
                        v-model="formData.description"
                        placeholder="Describe your business in detail..."
                        class="form-textarea"
                        :class="{ 'error': errors.description }"
                        rows="4"
                      ></textarea>
                    </div>
                    <span v-if="errors.description" class="error-message">{{ errors.description }}</span>
                  </div>
                </div>
              </div>

              <!-- Contact Information Section -->
              <div class="profile-card">
                <div class="card-header">
                  <h3 class="card-title">Contact Information</h3>
                  <p class="card-subtitle">Business address and email</p>
                </div>
                <div class="card-body">
                  <div class="form-group">
                    <label for="address" class="form-label">Business Address</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <input
                        id="address"
                        v-model="formData.address"
                        type="text"
                        placeholder="e.g., 1 Hacker Way, Menlo Park, CA 94025"
                        class="form-input"
                        :class="{ 'error': errors.address }"
                      />
                    </div>
                    <span v-if="errors.address" class="error-message">{{ errors.address }}</span>
                  </div>

                  <div class="form-group">
                    <label for="email" class="form-label">Business Email</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      <input
                        id="email"
                        v-model="formData.email"
                        type="email"
                        placeholder="e.g., contact@luckyshrub.com"
                        class="form-input"
                        :class="{ 'error': errors.email }"
                      />
                    </div>
                    <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
                  </div>
                </div>
              </div>

              <!-- Websites Section -->
              <div class="profile-card">
                <div class="card-header">
                  <h3 class="card-title">Websites</h3>
                  <p class="card-subtitle">Add up to 2 website URLs (max 256 characters each)</p>
                </div>
                <div class="card-body">
                  <div class="form-group">
                    <label for="website1" class="form-label">Website 1</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                      <input
                        id="website1"
                        v-model="formData.websites[0]"
                        type="url"
                        placeholder="https://www.example.com"
                        class="form-input"
                        :class="{ 'error': errors.website1 }"
                        maxlength="256"
                      />
                    </div>
                    <span v-if="errors.website1" class="error-message">{{ errors.website1 }}</span>
                  </div>

                  <div class="form-group">
                    <label for="website2" class="form-label">Website 2 (Optional)</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                      <input
                        id="website2"
                        v-model="formData.websites[1]"
                        type="url"
                        placeholder="https://www.example2.com"
                        class="form-input"
                        :class="{ 'error': errors.website2 }"
                        maxlength="256"
                      />
                    </div>
                    <span v-if="errors.website2" class="error-message">{{ errors.website2 }}</span>
                  </div>
                </div>
              </div>

              <!-- Submit Button -->
              <div class="form-actions">
                <button
                  type="submit"
                  class="update-button"
                  :disabled="isUpdating"
                  :class="{ 'loading': isUpdating }"
                >
                  <span v-if="!isUpdating">Update Business Profile</span>
                  <span v-else class="loading-spinner">
                    <span class="spinner"></span>
                    Updating...
                  </span>
                </button>
              </div>
            </form>

          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { AuthService } from '@/services/auth'
import { toast } from '@/utils/toast'
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'

export default {
  name: 'WhatsAppProfileView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      profile: {},
      formData: {
        about: '',
        address: '',
        description: '',
        email: '',
        vertical: '',
        websites: ['', '']
      },
      displayName: '',
      newDisplayName: '',
      phoneNumber: '',
      selectedPhoto: null,
      profilePhotoPreview: null,
      isLoading: true,
      isUpdating: false,
      isUploadingPhoto: false,
      isUpdatingDisplayName: false,
      errors: {}
    }
  },
  async mounted () {
    await this.loadProfile()
    await this.loadDisplayName()
  },
  methods: {
    async loadProfile () {
      try {
        this.isLoading = true

        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        const response = await fetch('https://api.quali-d.com/whatsapp-profile', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to load WhatsApp Business Profile')
        }

        const data = await response.json()

        if (data.success && data.profile) {
          this.profile = data.profile

          // Populate form data
          this.formData = {
            about: data.profile.about || '',
            address: data.profile.address || '',
            description: data.profile.description || '',
            email: data.profile.email || '',
            vertical: data.profile.vertical || '',
            websites: [
              data.profile.websites && data.profile.websites[0] ? data.profile.websites[0] : '',
              data.profile.websites && data.profile.websites[1] ? data.profile.websites[1] : ''
            ]
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error)
        toast.error(error.message || 'Failed to load WhatsApp Business Profile')
      } finally {
        this.isLoading = false
      }
    },

    async loadDisplayName () {
      try {
        const token = AuthService.getAuthToken()
        if (!token) return

        const response = await fetch('https://api.quali-d.com/whatsapp-display-name', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          if (data.success) {
            this.displayName = data.display_name || ''
            this.phoneNumber = data.phone_number || ''
            this.newDisplayName = this.displayName
          }
        }
      } catch (error) {
        console.error('Error loading display name:', error)
      }
    },

    async updateDisplayName () {
      if (!this.newDisplayName || this.newDisplayName.trim() === '') {
        toast.error('Display name cannot be empty')
        return
      }

      if (this.newDisplayName === this.displayName) {
        toast.error('Please enter a different display name')
        return
      }

      this.isUpdatingDisplayName = true

      try {
        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        const response = await fetch('https://api.quali-d.com/whatsapp-display-name', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            display_name: this.newDisplayName
          })
        })

        const data = await response.json()

        if (data.success && data.details) {
          // Display name request submitted successfully
          const message = `
            <div style="text-align: center;">
              <div style="width: 64px; height: 64px; background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                          border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px;">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h3 style="margin-bottom: 16px; color: #1f2937;">Display Name Request Submitted!</h3>
              <p style="margin-bottom: 20px; color: #6b7280; font-size: 15px;">${data.message}</p>

              <div style="background: #f9fafb; border-radius: 12px; padding: 20px; margin: 24px 0; text-align: left;">
                <div style="margin-bottom: 16px;">
                  <strong style="color: #374151; font-size: 14px;">Requested Name:</strong>
                  <div style="color: #10b981; font-size: 18px; font-weight: 600; margin-top: 4px;">${data.details.requested_name}</div>
                </div>
                <div style="margin-bottom: 16px;">
                  <strong style="color: #374151; font-size: 14px;">Status:</strong>
                  <div style="color: #f59e0b; font-size: 16px; font-weight: 600; margin-top: 4px; text-transform: capitalize;">
                    ${data.details.status.replace('_', ' ')}
                  </div>
                </div>
                <div>
                  <strong style="color: #374151; font-size: 14px;">Approval Time:</strong>
                  <div style="color: #6b7280; font-size: 15px; margin-top: 4px;">${data.details.approval_time}</div>
                </div>
              </div>

              <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 16px; text-align: left;">
                <strong style="color: #1e40af; font-size: 14px; display: flex; align-items: center; margin-bottom: 8px;">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  What Happens Next:
                </strong>
                <ul style="margin: 0; padding-left: 20px; color: #1e40af; font-size: 13px; line-height: 1.6;">
                  ${data.details.notes.map(note => `<li style="margin: 4px 0;">${note}</li>`).join('')}
                </ul>
              </div>
            </div>
          `
          this.showInstructionsDialog(message)
          toast.success(data.message)
        } else if (data.success === false && data.fallback_instructions) {
          // Permission error - show fallback instructions
          let instructionsList = '<ol style="text-align: left; padding-left: 20px; margin: 20px 0; color: #374151;">'
          Object.values(data.fallback_instructions).forEach(instruction => {
            instructionsList += `<li style="margin: 12px 0;">${instruction}</li>`
          })
          instructionsList += '</ol>'

          const message = `
            <div style="text-align: center;">
              <h3 style="margin-bottom: 16px; color: #1f2937;">Update Display Name via Meta Business Suite</h3>
              <p style="margin-bottom: 20px; color: #6b7280;">${data.message}</p>
              ${instructionsList}
              <a href="${data.direct_link}" target="_blank" rel="noopener noreferrer"
                 style="display: inline-block; margin-top: 20px; padding: 12px 24px; background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                        color: white; text-decoration: none; border-radius: 8px; font-weight: 600;">
                Open Meta Business Suite →
              </a>
            </div>
          `
          this.showInstructionsDialog(message)
        } else if (!data.success) {
          throw new Error(data.message || 'Failed to submit display name change')
        }
      } catch (error) {
        console.error('Error updating display name:', error)
        toast.error(error.message || 'Failed to get display name change instructions', 7000)
      } finally {
        this.isUpdatingDisplayName = false
      }
    },

    showInstructionsDialog (htmlContent) {
      // Create a simple modal overlay
      const overlay = document.createElement('div')
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        padding: 20px;
      `

      const modal = document.createElement('div')
      modal.style.cssText = `
        background: white;
        border-radius: 16px;
        padding: 32px;
        max-width: 600px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        position: relative;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      `

      const closeBtn = document.createElement('button')
      closeBtn.innerHTML = '×'
      closeBtn.style.cssText = `
        position: absolute;
        top: 16px;
        right: 16px;
        background: none;
        border: none;
        font-size: 32px;
        cursor: pointer;
        color: #6b7280;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: all 0.2s;
      `
      closeBtn.onmouseover = () => {
        closeBtn.style.background = '#f3f4f6'
        closeBtn.style.color = '#1f2937'
      }
      closeBtn.onmouseout = () => {
        closeBtn.style.background = 'none'
        closeBtn.style.color = '#6b7280'
      }
      closeBtn.onclick = () => document.body.removeChild(overlay)

      modal.innerHTML = htmlContent
      modal.insertBefore(closeBtn, modal.firstChild)
      overlay.appendChild(modal)
      document.body.appendChild(overlay)

      // Close on overlay click
      overlay.onclick = (e) => {
        if (e.target === overlay) {
          document.body.removeChild(overlay)
        }
      }
    },

    validateForm () {
      this.errors = {}
      let isValid = true

      // Validate about (max 139 characters)
      if (this.formData.about && this.formData.about.length > 139) {
        this.errors.about = 'About status must be 139 characters or less'
        isValid = false
      }

      // Validate email format if provided
      if (this.formData.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(this.formData.email)) {
          this.errors.email = 'Please enter a valid email address'
          isValid = false
        }
      }

      // Validate website URLs if provided
      if (this.formData.websites[0]) {
        try {
          const url = new URL(this.formData.websites[0])
          if (!url.protocol.startsWith('http')) {
            this.errors.website1 = 'Website must start with http:// or https://'
            isValid = false
          }
        } catch {
          this.errors.website1 = 'Please enter a valid URL'
          isValid = false
        }
      }

      if (this.formData.websites[1]) {
        try {
          const url = new URL(this.formData.websites[1])
          if (!url.protocol.startsWith('http')) {
            this.errors.website2 = 'Website must start with http:// or https://'
            isValid = false
          }
        } catch {
          this.errors.website2 = 'Please enter a valid URL'
          isValid = false
        }
      }

      return isValid
    },

    async updateProfile () {
      if (!this.validateForm()) {
        return
      }

      this.isUpdating = true

      try {
        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        // Filter out empty websites
        const websites = this.formData.websites.filter(url => url && url.trim() !== '')

        const response = await fetch('https://api.quali-d.com/whatsapp-profile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            about: this.formData.about,
            address: this.formData.address,
            description: this.formData.description,
            email: this.formData.email,
            vertical: this.formData.vertical,
            websites: websites
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to update WhatsApp Business Profile')
        }

        const data = await response.json()

        if (data.success !== true) {
          throw new Error(data.message || 'Failed to update profile')
        }

        toast.success('WhatsApp Business Profile updated successfully!')

        // Reload profile to get updated data
        setTimeout(() => {
          this.loadProfile()
        }, 3000)
      } catch (error) {
        console.error('Error updating profile:', error)
        toast.error(error.message || 'Failed to update WhatsApp Business Profile', 7000)
      } finally {
        this.isUpdating = false
      }
    },

    handlePhotoSelect (event) {
      const file = event.target.files[0]
      if (!file) return

      // Validate file type
      if (!['image/jpeg', 'image/jpg'].includes(file.type)) {
        toast.error('Profile picture must be a JPG/JPEG image')
        event.target.value = ''
        return
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Profile picture must be less than 5MB')
        event.target.value = ''
        return
      }

      // Validate minimum size (63KB recommended)
      if (file.size < 63 * 1024) {
        toast.error('Profile picture should be at least 63KB for best quality')
        event.target.value = ''
        return
      }

      this.selectedPhoto = file

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        this.profilePhotoPreview = e.target.result
      }
      reader.readAsDataURL(file)
    },

    async uploadProfilePicture () {
      if (!this.selectedPhoto) return

      this.isUploadingPhoto = true

      try {
        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        const formData = new FormData()
        formData.append('profile_picture', this.selectedPhoto)

        const response = await fetch('https://api.quali-d.com/whatsapp-profile/upload-picture', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: formData
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to upload profile picture')
        }

        const data = await response.json()

        if (data.success !== true) {
          throw new Error(data.message || 'Failed to upload profile picture')
        }

        toast.success('Profile picture updated successfully!')
        this.selectedPhoto = null

        // Reload profile to get updated picture URL
        setTimeout(() => {
          this.loadProfile()
          this.profilePhotoPreview = null
        }, 2000)
      } catch (error) {
        console.error('Error uploading profile picture:', error)
        toast.error(error.message || 'Failed to upload profile picture', 7000)
      } finally {
        this.isUploadingPhoto = false
      }
    }
  }
}
</script>

<style scoped>
.whatsapp-profile-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.whatsapp-profile-container {
  max-width: 900px;
  margin: 0 auto;
}

.profile-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 16px;
}

.spinner-large {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(59, 130, 246, 0.2);
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.profile-card {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--border-primary);
  overflow: hidden;
}

.card-header {
  padding: 24px 24px 0;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.card-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.card-body {
  padding: 24px;
}

.profile-photo-section {
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
}

.photo-container {
  flex-shrink: 0;
}

.photo-wrapper {
  position: relative;
  width: 120px;
  height: 120px;
}

.profile-photo {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--border-primary);
  display: block;
}

.profile-photo-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
  border: 3px solid var(--border-primary);
}

.photo-upload-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(59, 130, 246, 0.95);
  color: white;
  padding: 8px;
  border-radius: 0 0 60px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  transition: all 0.2s;
  opacity: 0;
}

.photo-wrapper:hover .photo-upload-overlay {
  opacity: 1;
}

.photo-upload-overlay svg {
  width: 18px;
  height: 18px;
}

.photo-upload-overlay span {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.photo-input-hidden {
  display: none;
}

.photo-info {
  flex: 1;
  min-width: 200px;
}

.photo-note {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 4px 0;
}

.upload-photo-btn {
  margin-top: 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.upload-photo-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.upload-photo-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  width: 18px;
  height: 18px;
  opacity: 0.6;
  z-index: 2;
  color: var(--text-secondary);
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 16px;
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  font-size: 16px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s;
  outline: none;
  font-family: inherit;
}

.form-input {
  padding-left: 52px;
}

select.form-input {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 48px;
}

.form-input:focus,
.form-textarea:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.error,
.form-textarea.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.char-count {
  font-size: 12px;
  color: var(--text-tertiary);
  text-align: right;
  margin-top: 4px;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 14px;
  margin-top: 6px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}

.update-button {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.update-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

.update-button:disabled {
  opacity: 0.8;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-message {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #059669;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.error-message-card {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.display-name-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.current-display-name {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.display-name-value {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--bg-secondary);
  border-radius: 12px;
  border: 2px solid var(--border-primary);
  color: var(--text-primary);
}

.display-name-value svg:first-child {
  color: var(--text-secondary);
  flex-shrink: 0;
}

.display-name-text {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
}

.display-name-placeholder {
  font-size: 16px;
  color: var(--text-tertiary);
  font-style: italic;
  flex: 1;
}

.verified-badge {
  flex-shrink: 0;
  filter: drop-shadow(0 0 4px rgba(16, 185, 129, 0.3));
}

.phone-number-hint {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 4px 0 0;
  padding-left: 4px;
}

.new-display-name {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.display-name-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 13px;
  color: #1e40af;
  line-height: 1.5;
}

.display-name-note svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: #3b82f6;
}

.update-display-name-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  align-self: flex-start;
}

.update-display-name-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.update-display-name-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    margin-top: 64px;
  }

  .whatsapp-profile-container {
    padding: 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .profile-photo-section {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-actions {
    justify-content: stretch;
  }

  .update-button {
    width: 100%;
  }
}
</style>
